import { computed, inject, Injectable } from '@angular/core';
import { Requirement } from '../components/side-nav-menu/menu-types';
import { FeatureItemDef, MenuSectionConfig, campaignToViewModel } from '../components/side-nav-menu/menu-grammar';
import { SubscriptionStore } from '../stores/subscription-store';
import { CourseStore } from '../stores/course-store';
import { GradeBoosterStore } from '../stores/grade-booster-store';
import { FeatureStore } from '../stores/feature-store';
import { OnboardingStore } from '../stores/onboarding-store';
import { EssaySkillTryStore } from '../stores/essay-skill-try-store';
import { CampaignStore } from '../stores/campaign-store';
import { LiveLessonService } from './live-lesson.service';

export enum MenuContext {
  SUBSCRIBED = 'subscribed',
  FREE_TASTER = 'freeTaster',
  /** No feature items shown — unsubscribed and still mid-onboarding. */
  ONBOARDING = 'onboarding',
}

export type FeatureItem = FeatureItemDef & {
  requires?: Requirement;
  lockedRequirement?: Requirement;
};

export type MenuDefinition = {
  context: MenuContext;
  items: FeatureItem[];
};

const subscribedItems: FeatureItem[] = [
  {
    displayName: 'Mock Last Min Revision',
    tooltip: 'Mock Last-Minute Revision',
    icon: '🚀',
    materialIcon: true,
    url: '/lmr-mocks',
    featured: true,
  },
  {
    displayName: 'My Courses',
    tooltip: 'My Courses',
    icon: 'assets/images/course-placeholder.svg',
    materialIcon: false,
    monochrome: true,
    url: '/dashboard',
    requires: Requirement.HASCOURSES,
  },
  {
    displayName: 'Live Lessons',
    tooltip: 'Access live tutoring sessions',
    icon: 'video_call',
    materialIcon: true,
    url: '/live-lesson',
    // Gated on package ownership (see canViewMenuItem) — a non-entitled user
    // doesn't see this item at all. The route itself is untouched: it's
    // still reachable directly (e.g. a future CTA into the non-premium
    // iframe), still gated by liveLessonAccessGuard, which redirects a
    // non-entitled visitor to /live-lesson/non-premium rather than blocking
    // them outright.
    requires: Requirement.LIVELESSONS_LIVE,
  },
  {
    displayName: 'Grade Jump Q+A',
    tooltip: 'Ask Aaliyah a question and browse past answers',
    icon: 'assets/images/message-question-circle.png',
    materialIcon: false,
    monochrome: true,
    url: '/ask-aaliyah',
    // Gated on package ownership (see canViewMenuItem) — a non-entitled user
    // doesn't see this item at all. The route itself is untouched: it's
    // still reachable directly (e.g. a future CTA into the non-premium
    // iframe), still gated by askAaliyahAccessGuard, which redirects a
    // non-entitled visitor to /ask-aaliyah/non-premium rather than blocking
    // them outright.
    requires: Requirement.ASKAALIYAH_LIVE,
  },
  {
    displayName: 'Essay Marking',
    tooltip: 'Mark your writing and track your essay skills progress',
    icon: 'edit_note',
    materialIcon: true,
    url: '/essay-marking',
    // Gated on whether the feature is live at all. Once live, any
    // subscribed user can browse every essay marking course — the API
    // decides per-skill whether the content is "standard" (included) or
    // needs the essay-skills-feature.
    requires: Requirement.ESSAYMARKING_LIVE,
  },
];

const freeTasterItems: FeatureItem[] = [
  // Temporarily removed: 'Taster Lessons' (linked to /free-taster). Restore
  // by un-commenting this entry.
  // {
  //   displayName: 'Taster Lessons',
  //   tooltip: 'Taster Lessons',
  //   icon: 'assets/images/graduation-hat.svg',
  //   materialIcon: false,
  //   url: '/free-taster',
  //   pill: '• 5 Free!',
  // },
  {
    displayName: 'Free Taster',
    tooltip: 'Try a free taster lesson',
    icon: 'assets/images/graduation-hat.svg',
    materialIcon: false,
    monochrome: true,
    url: '/free-taster/viewer',
  },
  {
    displayName: 'Live Lessons',
    tooltip: 'Access live tutoring sessions',
    icon: 'video_call',
    materialIcon: true,
    url: '/live-lesson',
    requires: Requirement.LIVELESSONS_LIVE,
  },
];

const menus: MenuDefinition[] = [
  { context: MenuContext.SUBSCRIBED, items: subscribedItems },
  { context: MenuContext.FREE_TASTER, items: freeTasterItems },
  { context: MenuContext.ONBOARDING, items: [] },
];

@Injectable({ providedIn: 'root' })
export class MenuService {
  private subscriptionStore = inject(SubscriptionStore);
  private courseStore = inject(CourseStore);
  private gradeBoosterStore = inject(GradeBoosterStore);
  private featureStore = inject(FeatureStore);
  private onboardingStore = inject(OnboardingStore);
  private essaySkillTryStore = inject(EssaySkillTryStore);
  private campaignStore = inject(CampaignStore);
  private liveLessonService = inject(LiveLessonService);

  activeContext = computed<MenuContext>(() => {
    // A pending subscription transfer freezes the account regardless of
    // subscription status — hide functional items even though they're
    // still technically subscribed until the transfer resolves.
    if (this.onboardingStore.isTransferFrozen() === true) {
      return MenuContext.ONBOARDING;
    }

    if (this.subscriptionStore.isSubscribed()) {
      return MenuContext.SUBSCRIBED;
    }

    // Unsubscribed: keep the menu locked down until onboarding is confirmed
    // complete (treat "not yet checked" the same as "incomplete" so nothing
    // flashes visible before we know for sure).
    if (this.onboardingStore.isComplete() !== true) {
      return MenuContext.ONBOARDING;
    }

    return MenuContext.FREE_TASTER;
  });

  /**
   * Mid-onboarding, a parent/educator can sit for a while between finishing
   * their free taster (year group/courses/weakest course) and finishing the
   * rest of the wizard (transfer, exam boards, courses) — `activeContext`
   * stays ONBOARDING that whole time. They should still be able to reach
   * Packages once they've seen the taster, even though the rest of the menu
   * stays hidden until onboarding is fully complete. Excluded for a frozen
   * transfer (account's on hold — not the moment to push more packages) and
   * for anyone already subscribed (they get the full menu instead).
   */
  showPackagesInOnboarding = computed<boolean>(() => {
    if (this.onboardingStore.isTransferFrozen() === true) return false;
    if (this.subscriptionStore.isSubscribed()) return false;
    return (
      this.onboardingStore.isComplete() !== true &&
      this.onboardingStore.hasReachedFreeTaster() === true
    );
  });

  packagesOnlySection = computed<MenuSectionConfig>(() => ({
    key: 'packages',
    items: [
      {
        id: 'packages',
        icon: 'inventory_2',
        iconType: 'material',
        title: 'Packages',
        url: '/packages',
        locked: false,
      },
    ],
  }));

  /**
   * Live campaigns are shown to every user regardless of plan (subscribed-menu and
   * free-taster-menu both render campaignSection unconditionally already, with no
   * Requirement/plan gate at all) — the only gap is mid-onboarding, where neither of those
   * components is mounted at all. Same "not before the free taster" rule as
   * showPackagesInOnboarding(), but — unlike Packages, which is a purchase nudge that doesn't
   * make sense to an already-subscribed user — a live campaign is informational, not a sales
   * pitch, so it's shown regardless of subscription status once the free taster's been reached,
   * including to an already-subscribed user whose only reason for being in ONBOARDING context
   * is a frozen transfer.
   */
  showCampaignsInOnboarding = computed<boolean>(() => {
    return (
      this.onboardingStore.isComplete() !== true &&
      this.onboardingStore.hasReachedFreeTaster() === true
    );
  });

  campaignsOnlySection = computed<MenuSectionConfig>(() => ({
    key: 'campaigns',
    items: this.campaignStore.campaigns().map((campaign) => campaignToViewModel(campaign)),
  }));

  /**
   * Same gap as showPackagesInOnboarding(): once a parent/educator has seen their free taster
   * (year group/courses/weakest course) but hasn't finished the rest of the wizard,
   * activeContext() is still ONBOARDING, so the freeTaster menu (and its Free Taster link) never
   * mounts even though /free-taster/viewer is already reachable to them. Excluded once
   * subscribed or transfer-frozen — an active plan supersedes the taster, and a frozen transfer
   * shouldn't offer a taster deep-link to someone who's actually already paid. No isComplete()
   * check (unlike Packages/Campaigns): once onboarding does complete, activeContext() itself
   * flips to FREE_TASTER and free-taster-menu takes over, so this only needs to cover the
   * mid-wizard gap.
   */
  showFreeTasterInOnboarding = computed<boolean>(() => {
    if (this.onboardingStore.isTransferFrozen() === true) return false;
    if (this.subscriptionStore.isSubscribed()) return false;
    return this.onboardingStore.hasReachedFreeTaster() === true;
  });

  showLiveLessonsInOnboarding = computed<boolean>(() => {
    if (this.onboardingStore.isTransferFrozen() === true) return false;
    return (
      this.featureStore.isFeatureLive('live-lessons-feature') &&
      (
        this.featureStore.hasFeature('live-lessons-feature') ||
        (this.subscriptionStore.isSubscribed() && this.featureStore.isInAccessWindow('live-lessons-feature', 'standard')) ||
        this.featureStore.isInAccessWindow('live-lessons-feature', 'free')
      )
    );
  });

  freeTasterOnlySection = computed<MenuSectionConfig>(() => ({
    key: 'freeTaster',
    items: [
      {
        id: 'freeTaster',
        icon: 'assets/images/graduation-hat.svg',
        iconType: 'image',
        title: 'Free Taster',
        url: '/free-taster/viewer',
        locked: false,
      },
    ],
  }));

  liveLessonsOnlySection = computed<MenuSectionConfig>(() => ({
    key: 'liveLessons',
    items: [
      {
        id: 'live-lessons',
        icon: 'video_call',
        iconType: 'material',
        title: 'Live Lessons',
        url: '/live-lesson',
        locked: false,
      },
    ],
  }));

  private activeMenu = computed<MenuDefinition>(() => {
    const context = this.activeContext();
    return menus.find((m) => m.context === context) ?? menus[0];
  });

  /**
   * A standard-plan user gets a flat number of free essay marking tries across every essay skill
   * (not per skill) — shown as one gift emoji per try on the Essay Marking menu item so a mobile
   * user, who's steered towards a laptop/iPad for the actual marking experience, still has a
   * reminder they've got free tries waiting next time they're on a bigger screen. Absent entirely
   * once the essay-skills-feature is owned (unlimited, nothing to count down) or once tries hit 0.
   */
  menuItems = computed(() => {
    const items = this.activeMenu().items;
    const triesRemaining = this.essaySkillTryStore.triesRemaining();

    if (this.featureStore.bypassesEssaySkillsTries() || triesRemaining === null) {
      return items;
    }

    return items.map((item) =>
      item.url === '/essay-marking'
        ? { ...item, pill: '🎁'.repeat(Math.max(0, triesRemaining)) || undefined }
        : item,
    );
  });

  canViewMenuItem(requirement?: Requirement): boolean {
    switch (requirement) {
      case Requirement.HASCOURSES:
        return this.courseStore.entitledCourses().length > 0;
      // Live Lessons/Ask Aaliyah: unlike Essay Marking (which any subscriber can browse, gated
      // per-skill by the API), these two are all-or-nothing package add-ons — the nav item is
      // hidden entirely unless the user actually owns the feature, not just because it's switched
      // on platform-wide. The iframe route itself stays reachable regardless (see the "requires"
      // comments on subscribedItems above), so a future CTA elsewhere in the app can still link a
      // non-entitled user into it.
      case Requirement.LIVELESSONS_LIVE: {
        if (!this.featureStore.isFeatureLive('live-lessons-feature')) return false;

        const hasFeature = this.featureStore.hasFeature('live-lessons-feature');
        const isSubscribed = this.subscriptionStore.isSubscribed();
        const inStandardWindow = isSubscribed && this.featureStore.isInAccessWindow('live-lessons-feature', 'standard');

        // Standard/premium entitlement is package-level — the nav item shows regardless of
        // whether a lesson happens to be scheduled right now (same reasoning as My Courses,
        // Essay Marking, etc.: you're paying for the feature, not for there being content
        // this exact moment).
        if (hasFeature || inStandardWindow) return true;

        // A free user only gets in via the time-boxed free access window, which can be open
        // before any access_tier: 'free' lesson has actually been published — showing the nav
        // item in that gap just links to an empty page, so it additionally requires at least
        // one lesson the free tier can actually watch.
        if (this.featureStore.isInAccessWindow('live-lessons-feature', 'free')) {
          return this.liveLessonService.allLessons().some((l) => l.access_tier === 'free');
        }

        return false;
      }
      case Requirement.ASKAALIYAH_LIVE:
        return (
          this.featureStore.isFeatureLive('ask-aaliyah-feature') &&
          this.featureStore.hasFeature('ask-aaliyah-feature')
        );
      case Requirement.ESSAYMARKING_LIVE:
        return this.featureStore.isFeatureLive('essay-skills-feature');
      case Requirement.GRADEBOOSTER:
        return this.gradeBoosterStore.hasGradeBooster();
      default:
        return true;
    }
  }

  isMenuItemLocked(item: FeatureItem): boolean {
    if (!item.lockedRequirement) return false;
    return !this.canViewMenuItem(item.lockedRequirement);
  }
}
